import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Mail, Phone, MapPin, Coins, CreditCard, ShoppingBasket, Sparkles, Send } from "lucide-react";
import { SendMessageDialog } from "@/components/SendMessageDialog";
import { CustomerMessages } from "@/components/CustomerMessages";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Panel, Empty } from "@/components/PageHeader";
import { TierBadge, StatusBadge } from "@/components/TierBadge";
import { PointsDialog } from "@/components/PointsDialog";
import { CustomerProfile } from "@/components/CustomerProfile";
import { api, eur, num, fdate, fdatetime, errMsg } from "@/lib/api";
import { FlagBadge, FlagButton } from "@/components/cards/FlagButton";

const Stat = ({ label, value, id }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4">
    <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
    <div className="mt-1.5 font-heading text-xl font-semibold text-slate-900" data-testid={id}>{value}</div>
  </div>
);

export default function CustomerDetail() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [flag, setFlag] = useState(null);
  const [msgOpen, setMsgOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const nav = useNavigate();
  const load = useCallback(() => api.get(`/customers/${id}`).then((r) => setD(r.data)).catch((e) => setError(errMsg(e))), [id]);
  const loadMsgs = useCallback(() => api.get(`/customers/${id}/messages`).then((r) => setMsgs(r.data)).catch(() => {}), [id]);
  useEffect(() => { load(); loadMsgs(); api.get(`/cards/flags/${id}`).then((r) => setFlag(r.data?.status ? r.data : null)).catch(() => {}); }, [load, loadMsgs, id]);

  if (error) return <div data-testid="customer-error" className="rounded-xl border border-rose-200 bg-rose-50 p-6 text-sm text-rose-700">{error}</div>;
  if (!d) return <Skeleton className="h-64" />;
  const c = d.customer;

  return (
    <div className="animate-rise">
      <Link to="/clienti" data-testid="back-to-customers" className="mb-6 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900"><ArrowLeft className="h-4 w-4" />Clienti</Link>
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-heading text-3xl font-semibold text-slate-900 sm:text-4xl" data-testid="customer-name">{c.name}</h1>
            <TierBadge tier={c.tier} testId="customer-tier" />
            <StatusBadge status={c.status} />
            <FlagBadge flag={flag} testId="customer-flag" />
            <FlagButton customerId={c.id} flag={flag} onChange={setFlag} />
          </div>
          <div className="mt-3 flex flex-wrap gap-4 text-sm text-slate-500">
            {c.email && <span className="flex items-center gap-1.5"><Mail className="h-4 w-4" />{c.email}</span>}
            {c.phone && <span className="flex items-center gap-1.5"><Phone className="h-4 w-4" />{c.phone}</span>}
            {c.city && <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" />{c.city}</span>}
            {c.card && <span className="flex items-center gap-1.5 font-mono text-xs" data-testid="customer-card"><CreditCard className="h-4 w-4" />{c.card}</span>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2"><Button variant="outline" data-testid="open-ai-button" onClick={() => nav(`/analisi-ia?scope=customer&id=${c.id}`)}><Sparkles className="mr-2 h-4 w-4" />Analisi IA</Button>
        <Button variant="outline" data-testid="open-promo-button" onClick={() => nav(`/promozioni?scope=customer&id=${c.id}`)}>Promozioni</Button>
        <Button variant="outline" data-testid="open-basket-button" onClick={() => nav(`/paniere?customer=${c.id}`)}><ShoppingBasket className="mr-2 h-4 w-4" />Paniere</Button>
        <Button variant="outline" data-testid="open-message-dialog-button" onClick={() => setMsgOpen(true)}><Send className="mr-2 h-4 w-4" />Invia messaggio</Button>
        <Button data-testid="open-points-dialog-button" onClick={() => setOpen(true)} className="bg-blue-600 hover:bg-blue-700"><Coins className="mr-2 h-4 w-4" />Gestisci punti</Button></div>
      </div>
      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="Saldo punti" value={num(c.points)} id="customer-points" />
        <Stat label="Spesa totale" value={eur(c.total_spent)} id="customer-spent" />
        <Stat label="Acquisti" value={num(c.tx_count)} id="customer-tx-count" />
        <Stat label="Cliente dal" value={fdate(c.created_at)} id="customer-since" />
      </div>
      <CustomerProfile profile={d.profile} />
      <Tabs defaultValue="tx">
        <TabsList>
          <TabsTrigger value="tx" data-testid="tab-transactions">Ultime transazioni ({d.transactions.length})</TabsTrigger>
          <TabsTrigger value="mv" data-testid="tab-movements">Movimenti punti ({d.movements.length})</TabsTrigger>
          <TabsTrigger value="msg" data-testid="tab-messages">Comunicazioni ({msgs.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="msg"><CustomerMessages rows={msgs} /></TabsContent>
        <TabsContent value="tx">
          <Panel testId="customer-transactions">
            {d.transactions.length === 0 ? <Empty text="Nessuna transazione." /> : d.transactions.map((t) => (
              <div key={t.id} className="flex items-center justify-between border-b border-slate-100 px-5 py-3 last:border-0">
                <div><div className="text-sm text-slate-800">{t.store || t.channel}</div><div className="text-xs text-slate-400">{fdatetime(t.created_at)} · {t.items} articoli · n. {t.receipt}</div></div>
                <div className="text-right"><div className="font-mono text-sm">{eur(t.amount)}</div><div className="text-xs text-blue-600">+{t.points_earned} pt</div></div>
              </div>
            ))}
          </Panel>
        </TabsContent>
        <TabsContent value="mv">
          <Panel testId="customer-movements">
            {d.movements.length === 0 ? <Empty text="Nessun movimento." /> : d.movements.map((m) => (
              <div key={m.id} className="flex items-center justify-between border-b border-slate-100 px-5 py-3 last:border-0">
                <div><div className="text-sm text-slate-800">{m.reason}</div><div className="text-xs text-slate-400">{fdatetime(m.created_at)} · {m.type}</div></div>
                <span className={`font-mono text-sm ${m.points >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{m.points >= 0 ? "+" : ""}{num(m.points)}</span>
              </div>
            ))}
          </Panel>
        </TabsContent>
      </Tabs>
      <PointsDialog open={open} onOpenChange={setOpen} customer={c} onDone={load} />
      <SendMessageDialog open={msgOpen} onOpenChange={setMsgOpen} customer={c} onSent={loadMsgs} />
    </div>
  );
}
